import React, { useState, useEffect, useRef } from "react";
import { Modal, Form, Input, Select, Upload, Button, Row, Col, message } from "antd";
import { UploadOutlined, CameraOutlined } from "@ant-design/icons";
import { QRCodeCanvas } from "qrcode.react";
import moment from "moment";
import { useSelector } from "react-redux";
import axios from "axios";
import { urlBadge, urlPhotoUpload, urlUploadPhoto } from "services/CONSTANTS";
import QRCodeModal from "./QRCodeModal";

const { Option } = Select;

const IDFormModal = ({ visible, onCancel, onFinish, initialValues, form }) => {
  const classification = Form.useWatch('classification', form) || initialValues?.classification || 'Resident';
  const [imageList, setImageList] = useState([]);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [checkingUpload, setCheckingUpload] = useState(false);
  const idNumber = form.getFieldValue("idNumber") || initialValues?.idNumber || "test123";
  const userDataStore = useSelector((x) => x.userDataStore);
  const lastUploadedFilenameRef = useRef(null);
  const [uploadedFilename, setUploadedFilename] = useState('');

  useEffect(() => {
    if (!initialValues) {
      form.resetFields();
      setImageList([]);
    } else {
      setImageList(
        initialValues.image
          ? [
              {
                uid: "-1",
                name: initialValues.image,
                status: "done",
                url: `${urlBadge}/${initialValues.image}`,
              },
            ]
          : []
      );
      // Define fallback values to avoid undefined in fields
      const cleanValues = {
        firstName: '',
        lastName: '',
        classification: 'Resident',
        dob: initialValues?.dob || null,
        issueDate: initialValues?.issueDate ?? moment(), // set today only if new
        expiresDate: null,
        address: '',
        city: '',
        state: '',
        zip: '',
        email: '',
        phone: '',
        image:
          initialValues.image
            ? [
                {
                  uid: "-1",
                  name: initialValues.image,
                  status: "done",
                  url: `${urlBadge}/${initialValues.image}`,
                },
              ]
            : [],
        ...initialValues, // overwrite defaults with real values
      };
      // If initialValues.image is a string, convert to array
      if (initialValues.image && typeof initialValues.image === 'string') {
        cleanValues.image = [
          {
            uid: "-1",
            name: initialValues.image,
            status: "done",
            url: `${urlBadge}/${initialValues.image}`,
          },
        ];
      }
      form.setFieldsValue(cleanValues);
    }
  }, [initialValues, form]);

  const handleUploadChange = ({ file, fileList }) => {
    // Revoke previous preview URLs
    imageList.forEach(file => {
      if (file.thumbUrl) {
        URL.revokeObjectURL(file.thumbUrl);
      }
    });

    const newFileList = Array.isArray(fileList)
      ? fileList.map(file => {
          // Patch the name if needed
          if (
            file.status === "done" &&
            file.url &&
            lastUploadedFilenameRef.current &&
            !file.name.startsWith("photo_")
          ) {
            return {
              ...file,
              name: lastUploadedFilenameRef.current,
            };
          }
          if (file.status === "done" && file.url && file.name && file.name.startsWith("photo_")) {
            return file;
          }
          if (file.originFileObj) {
            return {
              ...file,
              thumbUrl: URL.createObjectURL(file.originFileObj),
            };
          }
          return file;
        })
      : [];
    setImageList(newFileList);
    form.setFieldsValue({ image: newFileList });
  };

  const handleImageUpload = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append("token", userDataStore.token);
    formData.append("image", file);

    try {
      const res = await axios.post(urlUploadPhoto, formData);
      console.log('handleImageUpload Res', res.data);
      const photoName = res.data.filename; // only the filename
      lastUploadedFilenameRef.current = photoName; // store for patching
      setUploadedFilename(photoName);
      form.setFieldsValue({ uploadedFilename: photoName });

      const uploadedFileObj = {
        ...file,
        uid: file.uid,
        name: photoName, // set to generated filename
        status: "done",
        url: res.data.path, // for preview
      };
      setImageList([uploadedFileObj]);
      form.setFieldsValue({ image: [uploadedFileObj] });

      onSuccess();
    } catch (err) {
      console.error("Upload failed:", err);
      onError(err);
    }
  };

  // Add message listener for photo upload
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'photoUploaded' && event.data.idNumber === idNumber) {
        // Force a check for the new photo
        checkForNewPhoto();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [idNumber]);

  // Function to check for new photo upload
  const checkForNewPhoto = async () => {
    if (!checkingUpload && qrModalVisible) {
      setCheckingUpload(true);
      try {
        // Use the API endpoint to check for photo existence
        const response = await axios.get(`${urlUploadPhoto}?idNumber=${idNumber}`, {
          headers: {
            'Authorization': `Bearer ${userDataStore.token}`
          }
        });
        
        if (response.data && response.data.success) {
          // Photo exists, update the form
          const photoUrl = `${urlBadge}/${idNumber}.jpg`;
          const newImageList = [{
            uid: "-1",
            name: `${idNumber}.jpg`,
            status: "done",
            url: photoUrl,
          }];
          
          setImageList(newImageList);
          form.setFieldsValue({ image: newImageList });
          message.success("New photo uploaded successfully!");
          setQrModalVisible(false);
          setCheckingUpload(false);
          return true; // Photo found and updated
        }
      } catch (error) {
        console.error("Error checking for new photo:", error);
        // Don't show error message to user, just continue polling
      } finally {
        setCheckingUpload(false);
      }
      return false; // Photo not found
    }
  };

  // Set up interval to check for new photo
  useEffect(() => {
    let interval;
    let attempts = 0;
    const maxAttempts = 30; // 1 minute maximum (2 seconds * 30)

    if (qrModalVisible) {
      interval = setInterval(async () => {
        const photoFound = await checkForNewPhoto();
        attempts++;

        if (photoFound || attempts >= maxAttempts) {
          clearInterval(interval);
          if (attempts >= maxAttempts) {
            message.warning("Photo upload timed out. Please try again.");
          }
        }
      }, 2000); // Check every 2 seconds
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [qrModalVisible, idNumber]);

  // Add a cleanup effect to reset checking state when modal closes
  useEffect(() => {
    if (!qrModalVisible) {
      setCheckingUpload(false);
    }
  }, [qrModalVisible]);

  // Watch for classification changes and clear DOB/Expires if Employee
  useEffect(() => {
    if (classification === 'Employee') {
      form.setFieldsValue({ dob: null, expiresDate: null });
    }
  }, [classification, form]);

  return (
    <>
      <style>{`
        .circular-upload .ant-upload.ant-upload-select-picture-card {
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
          padding: 0 !important;
          margin: 0 !important;
        }
      `}</style>
      <Modal
        visible={visible}
        title={initialValues ? "Edit ID" : "Create New ID"}
        onCancel={() => {
          imageList.forEach(file => {
            if (file.thumbUrl) {
              URL.revokeObjectURL(file.thumbUrl);
            }
          });
          onCancel();
        }}
        destroyOnClose
        width={540}
        footer={null}
      >
        <div style={{ maxWidth: 400, margin: '0 auto' }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => {
              // Always use the uploadedFilename state for the image
              onFinish({ ...values, image: uploadedFilename });
            }}
          >
            <Form.Item name="image" hidden>
              <Input type="hidden" />
            </Form.Item>
            <Form.Item name="uploadedFilename" hidden>
              <Input type="hidden" />
            </Form.Item>

            {/* Circular Upload Area with Avatar Placeholder */}
            <Row justify="center" style={{ marginBottom: 24 }}>
              <Col>
                <Form.Item name="image" className="circular-upload" valuePropName="fileList" getValueFromEvent={e => (Array.isArray(e) ? e : e && e.fileList ? e.fileList : [])} style={{ marginBottom: 0 }}>
                  <Upload
                    listType="picture-card"
                    showUploadList={false}
                    customRequest={handleImageUpload}
                    fileList={imageList}
                    onChange={handleUploadChange}
                    accept="image/*"
                    style={{ background: 'transparent', boxShadow: 'none', padding: 0, margin: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                  >
                    {imageList.length >= 1 ? (
                      <div
                        style={{
                          width: 120,
                          height: 120,
                          borderRadius: '50%',
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#f5f5f5',
                          border: '2px solid #d9d9d9',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                        }}
                      >
                        <img
                          src={
                            (imageList[0] && (imageList[0].url || imageList[0].thumbUrl))
                              ? (imageList[0].url || imageList[0].thumbUrl)
                              : ''
                          }
                          alt="avatar"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          width: 120,
                          height: 120,
                          borderRadius: '50%',
                          background: '#f5f5f5',
                          border: '2px dashed #d9d9d9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          cursor: 'pointer',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                          gap: 8,
                        }}
                      >
                        {/* SVG Avatar Placeholder */}
                        <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="28" cy="28" r="28" fill="#e6e6e6" />
                          <ellipse cx="28" cy="22" rx="10" ry="10" fill="#bdbdbd" />
                          <ellipse cx="28" cy="44" rx="16" ry="8" fill="#bdbdbd" />
                        </svg>
                        <div style={{ color: '#888', fontWeight: 500, fontSize: 16 }}>Upload Image</div>
                      </div>
                    )}
                  </Upload>
                </Form.Item>
              </Col>
            </Row>

            {/* First Name | Last Name */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="First Name"
                  name="firstName"
                  rules={[{ required: true, message: "First Name is required." }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Last Name"
                  name="lastName"
                  rules={[{ required: true, message: "Last Name is required." }]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            {/* Street Address */}
            <Row>
              <Col span={24}>
                <Form.Item label="Street Address" name="address" rules={[{ required: true, message: "Street Address is required." }]}> 
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            {/* City | State | Zip */}
            <Row gutter={16}>
              <Col span={10}>
                <Form.Item label="City" name="city" rules={[{ required: true, message: "City is required." }]}> 
                  <Input />
                </Form.Item>
              </Col>
              <Col span={7}>
                <Form.Item label="State" name="state" rules={[{ required: true, message: "State is required." }]}> 
                  <Select placeholder="Select a state">
                    <Option value="NY">New York</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={7}>
                <Form.Item label="Zip" name="zip" rules={[{ required: true, message: "Zip is required." }]}> 
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            {/* Email | Phone */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[{ type: "email", message: "The input is not a valid email." }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Phone" name="phone">
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            {/* Date of Birth | Card Classification (swapped order) */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Date of Birth (MM/DD/YYYY)"
                  name="dob"
                  rules={[
                    { required: true, message: "Date of Birth is required." },
                    {
                      pattern: /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(19|20)\d\d$/,
                      message: "Please use MM/DD/YYYY format!",
                    },
                  ]}
                >
                  <Input placeholder="MM/DD/YYYY" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Card Classification"
                  name="classification"
                  rules={[{ required: true, message: "Classification is required." }]}
                >
                  <Select
                    placeholder="Select classification"
                  >
                    <Option value="Resident">Resident</Option>
                    <Option value="Employee">Employee</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* Save Button */}
            <Row justify="center" style={{ marginTop: 24 }}>
              <Col span={24}>
                <Button type="primary" htmlType="submit" block>
                  Save
                </Button>
              </Col>
            </Row>
          </Form>
        </div>

        <QRCodeModal
          visible={qrModalVisible}
          onCancel={() => setQrModalVisible(false)}
          idNumber={idNumber}
        />
      </Modal>
    </>
  );
};

export default IDFormModal;
